import * as Acc from "@radix-ui/react-accordion";
import { Plus } from "@phosphor-icons/react";
import { useSeo, useSite } from "@/hooks/useSite";
import ContentBody from "@/components/site/ContentBody";
import { Band, PageHero, SitePage } from "@/components/site/SiteBits";

// FAQ doc → groups [{group, items: [{q, a}]}]. Radix Accordion (keyboard accessible); the first question starts open.
function Group({ g, gi }) {
  return (
    <section aria-labelledby={`faq-g-${gi}`} data-testid={`faq-group-${gi}`}>
      <h2 id={`faq-g-${gi}`} className="s-coral mb-4 text-xs font-bold uppercase tracking-[0.16em]">{g.group}</h2>
      <div className="space-y-3">
        {g.items.map((it, i) => (
          <Acc.Item key={i} value={`${gi}-${i}`} className="s-card overflow-hidden" data-testid={`faq-item-${gi}-${i}`}>
            <Acc.Header>
              <Acc.Trigger className="s-focus group flex w-full items-center justify-between gap-4 rounded-[20px] px-6 py-5 text-left font-semibold" data-testid={`faq-trigger-${gi}-${i}`}>
                {it.q}
                <span className="s-chip-choice flex h-9 w-9 shrink-0 items-center justify-center rounded-full group-data-[state=open]:border-transparent group-data-[state=open]:bg-[var(--s-coral)] group-data-[state=open]:text-white" aria-hidden>
                  <Plus size={16} weight="bold" className="s-plus" /></span>
              </Acc.Trigger>
            </Acc.Header>
            <Acc.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <div className="s-muted px-6 pb-6 leading-7"><ContentBody body={it.a} testId={`faq-answer-${gi}-${i}`} /></div>
            </Acc.Content>
          </Acc.Item>))}
      </div>
    </section>
  );
}

export default function Faq() {
  const { data, error, loading, reload } = useSite();
  const f = data?.docs?.site_faq?.fields || {};
  const groups = data?.docs?.faq?.groups || [];
  useSeo("FAQ", f.hero_lead);
  return (
    <SitePage loading={loading} error={error} onRetry={reload} testId="faq-page">
      <PageHero chip={f.hero_chip} title={f.hero_title} accent={f.hero_accent} lead={f.hero_lead} testId="faq-hero" />
      <Acc.Root type="multiple" defaultValue={["0-0"]} className="mt-14 max-w-3xl space-y-12">
        {groups.map((g, gi) => <Group key={gi} g={g} gi={gi} />)}
      </Acc.Root>
      <Band title={f.band_title} accent={f.band_accent} line={f.band_line} button={f.band_button} to="/support" testId="faq-band" />
    </SitePage>
  );
}
